import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { haptic, HAPTIC_START, HAPTIC_STOP } from "@/lib/langs";

const MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];

export function useRecorder(onDone) {
  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);
  const rec = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const ctx = useRef(null);
  const raf = useRef(0);
  const startedAt = useRef(0);
  const peak = useRef(0);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  const cleanup = useCallback(() => {
    cancelAnimationFrame(raf.current);
    stream.current?.getTracks().forEach((t) => t.stop());
    ctx.current?.close().catch(() => {});
    stream.current = null;
    ctx.current = null;
    rec.current = null;
    setRecording(false);
    setLevel(0);
  }, []);

  const start = useCallback(async () => {
    if (rec.current) return;
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      stream.current = s;
      const mime = MIMES.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const r = new MediaRecorder(s, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      r.onstop = () => {
        haptic(HAPTIC_STOP);
        const dur = Date.now() - startedAt.current;
        const blob = new Blob(chunks.current, { type: (r.mimeType || "audio/webm").split(";")[0] });
        cleanup();
        if (dur <= 600) toast.message("Hold a little longer to record");
        else if (peak.current < 0.04) toast.error("No voice detected — check that your microphone is not muted or used by another app");
        else doneRef.current(blob);
      };
      rec.current = r;
      startedAt.current = Date.now();
      peak.current = 0;
      r.start();
      haptic(HAPTIC_START);
      setRecording(true);
      const ac = new (window.AudioContext || window.webkitAudioContext)();
      ctx.current = ac;
      const an = ac.createAnalyser();
      an.fftSize = 256;
      ac.createMediaStreamSource(s).connect(an);
      const arr = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        an.getByteFrequencyData(arr);
        const lv = Math.min(1, (arr.reduce((a, b) => a + b, 0) / arr.length / 255) * 2.2);
        if (lv > peak.current) peak.current = lv;
        setLevel(lv);
        raf.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      toast.error("Microphone unavailable or permission denied");
      cleanup();
    }
  }, [cleanup]);

  const stop = useCallback(() => {
    if (rec.current?.state === "recording") rec.current.stop();
  }, []);

  useEffect(() => () => {
    if (rec.current?.state === "recording") rec.current.onstop = null;
    cleanup();
  }, [cleanup]);

  return { recording, level, start, stop, isActive: () => !!rec.current };
}
